/**
 * The site chip panel and the site finder it becomes: a body's sites as chips
 * while there are a handful, and above that a search, filters that combine, a
 * pick on the globe and a sorted list drawn only where it is on screen; the
 * registry fetch that fills it and the selection it drives. At PLOT and below
 * it sits in the plot controls, above them in the Sites popup on the bottom bar.
 *
 * Reads the closure it was cut out of through `SiteChipsDeps`. The
 * `atlasCamera` probe members that read nothing outside this seam ride on
 * `probe`, which the composition root in src/atlas.ts folds back into the
 * one object the tools/preview/dojo arms read.
 */

import { escapeHtml } from '../../html.ts';
import { DTM_BASE } from '../dtmBase.ts';
import type { AtlasLive, AtlasState, Site } from '../../atlas';
import type { BodyReadersHandle } from '../bodyReaders';
import type { BodySwitchHandle } from '../bodySwitch';
import type { CameraRigHandle } from '../cameraRig';
import type { NavigationHandle } from '../navigation';
import {
  arcDeg, boxAround, buildSiteFacets, EMPTY_SITE_QUERY, matchSites, sortSites,
  type LatLonBox, type SiteFacets, type SiteQuery, type SiteSort,
} from '../siteFacets.ts';
import { defaultSiteIdx, type SiteRegistryState } from '../siteRegistry.ts';
import type { PlotControlsHandle } from './plotControls';
import { TEST_SWITCHES } from '../../urlFlags.ts';

export interface SiteChipsDeps {
  readonly SITE_LIST_BANDS_ABOVE: number;
  readonly overlay: HTMLElement;
  readonly state: AtlasState;
  normSiteQuery(s: string): string;
  selectSiteBody(id: string | null): void;
  siteBandOf(st: Site): string;
  syntheticSiteCount(): number;
  syntheticSites(template: Site, n: number, bodyRadiusM: number): Site[];
  syntheticSpread(): boolean;
  spreadSyntheticSites(real: readonly Site[], n: number, bodyId: string): Site[];
  /** The ground under the ship on the open body, or null with no ship to measure from. */
  subShipLatLon(): { lat: number; lon: number } | null;
  own(fn: () => void): void;
  /** The Layers list shares the finder's slot over the bottom bar. */
  closeLayers(): void;
  onOpenChange(open: boolean): void;
  readonly plotControls: PlotControlsHandle;
  readonly bodySwitch: BodySwitchHandle;
  readonly bodyReaders: BodyReadersHandle;
  /** Written from inside this seam. */
  readonly siteRegistry: SiteRegistryState;
  /** Written from inside this seam. */
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands them over through getters. */
  readonly cameraRig: CameraRigHandle;
  readonly navigation: NavigationHandle;
}

export interface SiteFinderProbe {
  readonly open: boolean;
  /** The filters fold under the search row. */
  readonly foldOpen: boolean;
  readonly armed: boolean;
  readonly many: boolean;
  readonly total: number;
  /** Registry ids of the list as drawn, in order, pinned open site excluded. */
  readonly shown: readonly string[];
  readonly rowsInDom: number;
  readonly sort: SiteSort;
  readonly pick: LatLonBox | null;
  readonly subShip: { lat: number; lon: number } | null;
  /** Milliseconds the last list build took, filter and sort and draw. */
  readonly lastRenderMs: number;
}

export interface SiteChipsHandle {
  ensureSites(): Promise<void>;
  /** `reveal` scrolls the list to the open site; without it a list rebuilt for a new query starts at its top. */
  renderSiteChips(reveal?: boolean): void;
  /** Clears the filter box, the filters and a globe pick, the way a body switch does. */
  resetFilter(): void;
  /** Puts the list where the tier wants it and shows the Sites button where it belongs. */
  syncFinder(): void;
  closeFinder(): void;
  readonly finderOpen: boolean;
  /** True between PICK ON GLOBE and the click on the body it waits for. */
  readonly globePickArmed: boolean;
  /** The finder's own answer to that click: the sites under a box round the point. */
  pickOnGlobe(lat: number, lon: number, halfKm: number): void;
  /** The `atlasCamera` members that read nothing outside this module. */
  readonly probe: {
    readonly sites: readonly string[] | null;
    readonly siteChips: { sites: number; selected: number; filter: string; rerender: () => void };
    readonly siteFinder: SiteFinderProbe;
  };
}

/** Pixels between the tops of two rows, beyond the row's own height. */
const ROW_GAP_PX = 3;
/** Rows drawn past each edge of the visible window, so a wheel notch never shows a gap. */
const ROW_OVERSCAN = 6;

export function createSiteChips(deps: SiteChipsDeps): SiteChipsHandle {
  const {
    SITE_LIST_BANDS_ABOVE, overlay,
    normSiteQuery, selectSiteBody, siteBandOf, syntheticSiteCount, syntheticSites,
  } = deps;
  const { elPlotCtl } = deps.plotControls;
  const { applyUrlSite, rebuildDescentTarget, refuseSite } = deps.bodySwitch;
  const { bodyQuads, ladderBody, quadFor, rowById } = deps.bodyReaders;

  const pcSites = overlay.querySelector('#pc-sites') as HTMLElement;
  const pcSitesHead = overlay.querySelector('#pc-sites-head') as HTMLElement;
  const pcSitesSearch = overlay.querySelector('#pc-sites-search') as HTMLElement;
  const pcSitesFilter = overlay.querySelector('#pc-sites-filter') as HTMLInputElement;
  const btnMore = overlay.querySelector('#pc-sites-more') as HTMLButtonElement;
  const pcSitesFacets = overlay.querySelector('#pc-sites-facets') as HTMLElement;
  const pcSitesPick = overlay.querySelector('#pc-sites-pick') as HTMLElement;
  const pcSitesPin = overlay.querySelector('#pc-sites-pin') as HTMLElement;
  const pcSitesList = overlay.querySelector('#pc-sites-list') as HTMLElement;
  const selNs = overlay.querySelector('#pc-sites-ns') as HTMLSelectElement;
  const selEw = overlay.querySelector('#pc-sites-ew') as HTMLSelectElement;
  const selChart = overlay.querySelector('#pc-sites-chart') as HTMLSelectElement;
  const selSource = overlay.querySelector('#pc-sites-source') as HTMLSelectElement;
  const selKind = overlay.querySelector('#pc-sites-kind') as HTMLSelectElement;
  const selSort = overlay.querySelector('#pc-sites-sort') as HTMLSelectElement;
  const btnGlobe = overlay.querySelector('#pc-sites-globe') as HTMLButtonElement;
  const plotAnchor = pcSites.nextElementSibling;

  // The finder: one button on the bottom bar, and the popup the list moves into.
  const sfHost = overlay.querySelector('#atlas-sf-host') as HTMLElement;
  const sfEl = document.createElement('div');
  sfEl.className = 'atlas-sf hidden';
  sfEl.innerHTML = '<button class="atlas-sf-head" id="atlas-sf-head" aria-expanded="false"'
    + ' title="Find a surveyed site on this body and go to it">Sites</button>'
    + '<div class="atlas-sf-body hidden" id="atlas-sf-body"></div>';
  sfHost.appendChild(sfEl);
  const sfHead = sfEl.querySelector('#atlas-sf-head') as HTMLButtonElement;
  const sfBody = sfEl.querySelector('#atlas-sf-body') as HTMLElement;
  let finderOpen = false;

  /** What the filter box holds, lowercased; empty shows every row. */
  let siteFilter = '';
  let facetQuery: Omit<SiteQuery, 'text' | 'pick'> = { ...EMPTY_SITE_QUERY };
  let pickBox: LatLonBox | null = null;
  let pickLabel = '';
  let globeArmed = false;
  let foldOpen = false;
  let sortBy: SiteSort = 'name';
  let facets: SiteFacets | null = null;
  let facetsFor: readonly Site[] | null = null;
  /** The list as drawn: registry indices in display order. */
  let shown: number[] = [];
  let lastRenderMs = 0;
  /** The row window last written to the list, so a scroll that keeps it rewrites nothing. */
  let firstDrawn = -1;
  let lastDrawn = -1;
  /** A reveal asked for while the list had no height, done when it gets one. */
  let revealPending = false;

  const devParam = (k: string): string | null => (TEST_SWITCHES && typeof location !== 'undefined'
    ? new URLSearchParams(location.search).get(k) : null);
  // `?sitechipsflat=1`: site-chips-check.mjs's control, the one unwrapped
  // row this panel used to be. DEV-only, so it drops in a build too.
  const flatSiteChips = devParam('sitechipsflat') === '1';
  // site-finder-check.mjs's controls: `?sitefinder=namequad` filters on name
  // and quad alone, as the list did before the finder; `?sitefinder=stale`
  // opens the row at the clicked position in the REGISTRY rather than the list.
  const finderInject = devParam('sitefinder');
  // `?ctlwidthoff=1`: the same file's width control, the panel with nothing
  // holding its width down, which is what let 200 chips take the whole pane.
  if (devParam('ctlwidthoff') === '1') elPlotCtl.classList.add('ctl-width-off');
  // `?scrolltrap=1`: site-chips-check.mjs's scroll control, the chip list
  // holding every wheel notch again instead of passing the ones at its own
  // top and bottom on to the panel.
  if (devParam('scrolltrap') === '1') pcSitesList.style.overscrollBehaviorY = 'contain';

  function siteChipHtml(st: Site, i: number, match: boolean): string {
    return `<button data-site="${i}" data-match="${match ? '1' : '0'}" class="${i === deps.siteRegistry.siteIdx ? 'on' : ''}" title="${escapeHtml(
      `${st.name}  ·  ${st.blurb}  ·  ${st.plots} plots · ${st.relief_m.toFixed(0)} m relief · ${st.region_mpp} m/px`,
    )}">${escapeHtml(st.name.toUpperCase())}</button>`;
  }

  function bodyRadiusKm(): number {
    return (rowById(deps.siteRegistry.siteBodyId)?.radius_m ?? 1_737_400) / 1000;
  }

  /** The facet table for the open body's rows, rebuilt when those rows change. */
  function ensureFacets(): SiteFacets {
    const rows = deps.siteRegistry.bodySites;
    if (facets && facetsFor === rows) return facets;
    const charted = bodyQuads().length > 0;
    facets = buildSiteFacets(rows, {
      norm: normSiteQuery,
      chartOf: (st) => {
        const q = charted ? quadFor(st.center_lat_deg, st.center_lon_deg) : undefined;
        return q ? { id: q.id, label: `${q.id} ${q.name.toUpperCase()}` } : { id: siteBandOf(st), label: siteBandOf(st) };
      },
      bodyRadiusKm: bodyRadiusKm(),
    });
    facetsFor = rows;
    fillSelects(facets, charted);
    return facets;
  }

  function options(sel: HTMLSelectElement, all: string, rows: readonly { id: string; label: string; count?: number }[]): void {
    const keep = sel.value;
    sel.innerHTML = `<option value="">${escapeHtml(all)}</option>` + rows.map((r) => (
      `<option value="${escapeHtml(r.id)}">${escapeHtml(r.count === undefined ? r.label : `${r.label} (${r.count})`)}</option>`
    )).join('');
    sel.value = rows.some((r) => r.id === keep) ? keep : '';
  }

  function fillSelects(f: SiteFacets, charted: boolean): void {
    options(selNs, 'NORTH AND SOUTH', [{ id: 'N', label: 'NORTH' }, { id: 'S', label: 'SOUTH' }]);
    options(selEw, 'EAST AND WEST', [{ id: 'E', label: 'EAST' }, { id: 'W', label: 'WEST' }]);
    // The Moon has no chart table in bodies.json, so it files by latitude band.
    options(selChart, charted ? 'EVERY CHART' : 'EVERY LATITUDE BAND', f.charts);
    options(selSource, 'EVERY SOURCE', f.sources);
    options(selKind, 'EVERY KIND', f.kinds);
    // East and west halves are how the Moon is read; Mars has its charts for that.
    selEw.classList.toggle('hidden', charted);
    readFacetSelects();
  }

  function readFacetSelects(): void {
    const ns = selNs.value;
    const ew = selEw.classList.contains('hidden') ? '' : selEw.value;
    const kind = selKind.value;
    facetQuery = {
      ns: ns === 'N' || ns === 'S' ? ns : '',
      ew: ew === 'E' || ew === 'W' ? ew : '',
      chart: selChart.value,
      source: selSource.value,
      kind: kind === 'landing' || kind === 'feature' || kind === 'canon' ? kind : '',
    };
    const s = selSort.value;
    sortBy = s === 'distance' || s === 'size' ? s : 'name';
  }

  function syncSortOptions(ship: { lat: number; lon: number } | null): void {
    const want = ship === null ? 'n' : 's';
    if (selSort.dataset['ship'] === want) return;
    selSort.dataset['ship'] = want;
    const keep = sortBy;
    selSort.innerHTML = '<option value="name">SORT BY NAME</option>'
      + `<option value="distance"${ship === null ? ' disabled' : ''}>SORT BY DISTANCE FROM THE SHIP</option>`
      + '<option value="size">SORT BY SIZE</option>';
    selSort.value = keep === 'distance' && ship === null ? 'name' : keep;
    sortBy = selSort.value === 'distance' || selSort.value === 'size' ? selSort.value : 'name';
    selSort.title = ship === null ? 'No ship position to measure from, so there is no distance to sort by.' : '';
  }

  /** The row's right-hand figure: what the list is sorted by, else its chart and source. */
  function rowMeta(i: number, ship: { lat: number; lon: number } | null): string {
    const st = deps.siteRegistry.bodySites[i];
    const r = facets?.rows[i];
    if (!st || !r) return '';
    if (sortBy === 'distance' && ship !== null) {
      const km = (arcDeg(ship.lat, ship.lon, st.center_lat_deg, st.center_lon_deg) * Math.PI / 180) * bodyRadiusKm();
      return `${Math.round(km).toLocaleString('en-US')} KM`;
    }
    if (sortBy === 'size') return `${Math.round(r.areaKm2).toLocaleString('en-US')} KM2`;
    return r.chart.length <= 6 ? r.chart : r.chart.split(' ')[0] ?? '';
  }

  function rowPitchPx(): number {
    const h = parseFloat(getComputedStyle(pcSitesList).getPropertyValue('--site-row-h'));
    return (Number.isFinite(h) && h > 0 ? h : 24) + ROW_GAP_PX;
  }

  /** Draws the rows the list's scroll position has on screen, and no others. */
  function paintRows(): void {
    const spacer = pcSitesList.firstElementChild as HTMLElement | null;
    if (!spacer || !pcSitesList.classList.contains('rows')) return;
    const pitch = rowPitchPx();
    const first = Math.max(0, Math.floor(pcSitesList.scrollTop / pitch) - ROW_OVERSCAN);
    const last = Math.min(shown.length, Math.ceil((pcSitesList.scrollTop + pcSitesList.clientHeight) / pitch) + ROW_OVERSCAN);
    if (first === firstDrawn && last === lastDrawn) return;
    firstDrawn = first;
    lastDrawn = last;
    const focused = document.activeElement instanceof HTMLElement && spacer.contains(document.activeElement)
      ? document.activeElement.dataset['site'] : undefined;
    const ship = sortBy === 'distance' ? deps.subShipLatLon() : null;
    const parts: string[] = [];
    for (let pos = first; pos < last; pos++) {
      const i = shown[pos];
      const st = i === undefined ? undefined : deps.siteRegistry.bodySites[i];
      if (i === undefined || !st) continue;
      parts.push(`<button data-site="${i}" data-pos="${pos}" data-match="1" class="atlas-site-row${i === deps.siteRegistry.siteIdx ? ' on' : ''}"`
        + ` style="top:${pos * pitch}px" title="${escapeHtml(`${st.name}. ${st.blurb}`)}">`
        + `<span class="atlas-site-n">${escapeHtml(st.name.toUpperCase())}</span>`
        + `<span class="atlas-site-m">${escapeHtml(rowMeta(i, ship))}</span></button>`);
    }
    spacer.innerHTML = parts.join('');
    if (focused !== undefined) {
      (spacer.querySelector(`button[data-site="${focused}"]`) as HTMLElement | null)?.focus({ preventScroll: true });
    }
  }

  /** Scrolls row `pos` into the list's box, draws it, and focuses it. */
  function focusRow(pos: number): void {
    const pitch = rowPitchPx();
    const top = pos * pitch;
    if (top < pcSitesList.scrollTop) pcSitesList.scrollTop = top;
    else if (top + pitch > pcSitesList.scrollTop + pcSitesList.clientHeight) {
      pcSitesList.scrollTop = top + pitch - pcSitesList.clientHeight;
    }
    paintRows();
    (pcSitesList.querySelector(`button[data-pos="${pos}"]`) as HTMLElement | null)?.focus({ preventScroll: true });
  }

  /** The list scrolled so the open site is on screen, wherever it sits. */
  function revealSelected(): void {
    if (pcSitesList.classList.contains('rows')) {
      const pos = shown.indexOf(deps.siteRegistry.siteIdx);
      if (pos < 0) return;
      const pitch = rowPitchPx();
      const top = pos * pitch;
      if (top < pcSitesList.scrollTop) pcSitesList.scrollTop = top;
      else if (top + pitch > pcSitesList.scrollTop + pcSitesList.clientHeight) {
        pcSitesList.scrollTop = top + pitch - pcSitesList.clientHeight;
      }
      paintRows();
      return;
    }
    const on = pcSitesList.querySelector('button.on') as HTMLElement | null;
    if (!on) return;
    const top = on.offsetTop - pcSitesList.offsetTop;
    if (top < pcSitesList.scrollTop) pcSitesList.scrollTop = top;
    else if (top + on.offsetHeight > pcSitesList.scrollTop + pcSitesList.clientHeight) {
      pcSitesList.scrollTop = top + on.offsetHeight - pcSitesList.clientHeight;
    }
  }

  /** The filters in force, as the head line names them. */
  function activeFilterWords(): string[] {
    const words: string[] = [];
    if (siteFilter !== '') words.push(`"${siteFilter.toUpperCase()}"`);
    for (const sel of [selNs, selEw, selChart, selSource, selKind]) {
      if (sel.value !== '' && !sel.classList.contains('hidden')) {
        words.push((sel.selectedOptions[0]?.textContent ?? sel.value).replace(/ \(\d+\)$/, ''));
      }
    }
    if (pickBox !== null) words.push('THE GLOBE PICK');
    return words;
  }

  function renderSiteChips(reveal = false): void {
    const t0 = performance.now();
    const n = deps.siteRegistry.bodySites.length;
    if (flatSiteChips) {
      pcSites.classList.add('flat');
      pcSitesHead.classList.add('hidden');
      pcSitesFilter.classList.add('hidden');
      pcSitesList.innerHTML = deps.siteRegistry.bodySites.map((st, i) => siteChipHtml(st, i, true)).join('');
      return;
    }
    // A handful of sites is the panel it always was: chips and nothing else.
    // The search, the filters and the list are what a body with thousands needs.
    const many = n > SITE_LIST_BANDS_ABOVE;
    pcSitesSearch.classList.toggle('hidden', !many);
    pcSitesFilter.classList.toggle('hidden', !many);
    pcSitesFacets.classList.toggle('hidden', !many || !foldOpen);
    if (!many && (siteFilter !== '' || pickBox !== null)) {
      siteFilter = '';
      pcSitesFilter.value = '';
      pickBox = null;
    }
    const f = ensureFacets();
    const ship = deps.subShipLatLon();
    syncSortOptions(ship);
    btnGlobe.classList.toggle('hidden', !(deps.state.tier === 1 || deps.state.tier === 2));
    btnGlobe.classList.toggle('on', globeArmed);
    btnGlobe.textContent = globeArmed ? 'CLICK THE GLOBE' : 'PICK ON GLOBE';
    // Folded filters still narrow the list, so the toggle counts the ones in force.
    const active = [selNs, selEw, selChart, selSource, selKind]
      .filter((sel) => sel.value !== '' && !sel.classList.contains('hidden')).length + (pickBox === null ? 0 : 1);
    btnMore.textContent = active > 0 ? `FILTERS (${active})` : 'FILTERS';
    btnMore.classList.toggle('on', foldOpen);
    btnMore.setAttribute('aria-expanded', foldOpen ? 'true' : 'false');
    pcSitesPick.classList.toggle('hidden', pickBox === null);
    if (pickBox !== null) {
      pcSitesPick.innerHTML = `<span>UNDER THE PICK AT ${escapeHtml(pickLabel)}</span>`
        + '<button data-pick-clear="1" title="Drop the globe pick">CLEAR</button>';
    }
    const query: SiteQuery = { ...facetQuery, text: many ? normSiteQuery(siteFilter) : '', pick: many ? pickBox : null };
    const matched = many
      ? sortSites(matchSites(f, query, finderInject === 'namequad'), sortBy, f, deps.siteRegistry.bodySites, ship)
      : deps.siteRegistry.bodySites.map((_, i) => i);
    const where = (rowById(ladderBody())?.display_name ?? deps.siteRegistry.siteBodyId ?? '').toUpperCase();
    const words = many ? activeFilterWords() : [];
    pcSitesHead.classList.toggle('hidden', !many);
    pcSitesHead.textContent = words.length === 0
      ? `${n} ${n === 1 ? 'SITE' : 'SITES'} ON ${where}`
      : `${matched.length} OF ${n} SITES ON ${where} MATCH ${words.join(', ')}`;

    // The open site is never filtered away: it is the ground the ladder is
    // aimed at, and a panel that hides it reads as though nothing is selected.
    const open = deps.siteRegistry.bodySites[deps.siteRegistry.siteIdx];
    const pinned = !!open && many && !matched.includes(deps.siteRegistry.siteIdx);
    pcSitesPin.classList.toggle('hidden', !pinned);
    pcSitesPin.innerHTML = pinned && open
      ? '<div class="atlas-sites-band">THE OPEN SITE, WHICH THIS FILTER DOES NOT MATCH</div>'
        + siteChipHtml(open, deps.siteRegistry.siteIdx, false)
      : '';

    shown = matched;
    if (!many) {
      pcSitesList.classList.remove('rows');
      pcSitesList.innerHTML = matched.map((i) => {
        const st = deps.siteRegistry.bodySites[i];
        return st ? siteChipHtml(st, i, true) : '';
      }).join('');
    } else if (matched.length === 0) {
      pcSitesList.classList.remove('rows');
      pcSitesList.innerHTML = `<div class="atlas-sites-empty">NO SITE ON ${escapeHtml(where)} MATCHES ${escapeHtml(words.join(', '))}</div>`;
    } else {
      pcSitesList.classList.add('rows');
      pcSitesList.innerHTML = `<div class="atlas-sites-spacer" style="height:${matched.length * rowPitchPx()}px"></div>`;
      firstDrawn = -1;
      lastDrawn = -1;
      paintRows();
    }
    if (reveal) {
      if (pcSitesList.clientHeight > 0) revealSelected();
      else revealPending = true;
    }
    lastRenderMs = performance.now() - t0;
  }

  function setFinderOpen(on: boolean): void {
    if (finderOpen === on) return;
    if (on) deps.closeLayers();
    finderOpen = on;
    sfEl.classList.toggle('open', on);
    sfBody.classList.toggle('hidden', !on);
    sfHead.setAttribute('aria-expanded', on ? 'true' : 'false');
    if (!on && globeArmed) globeArmed = false;
    // A list built while its box was closed drew no rows; drawn now it has a height.
    if (on) renderSiteChips(true);
    deps.onOpenChange(on);
  }

  function syncFinder(): void {
    const hasSites = deps.siteRegistry.bodySites.length > 0 && deps.bodySwitch.state.bodyRefused === null;
    const inPanel = deps.state.tier >= 5;
    const showButton = hasSites && !inPanel && deps.state.tier >= 1;
    sfEl.classList.toggle('hidden', !showButton);
    if (!showButton) setFinderOpen(false);
    const home = inPanel ? elPlotCtl : sfBody;
    if (pcSites.parentElement !== home) {
      if (inPanel) elPlotCtl.insertBefore(pcSites, plotAnchor && plotAnchor.parentElement === elPlotCtl ? plotAnchor : null);
      else sfBody.appendChild(pcSites);
      renderSiteChips(true);
    } else if (!btnGlobe.classList.contains('hidden') !== (deps.state.tier === 1 || deps.state.tier === 2)) {
      renderSiteChips();
    }
  }

  let sitesReady: Promise<void> | null = null;
  function ensureSites(): Promise<void> {
    // A refused body asks for nothing, the site registry included.
    if (deps.bodySwitch.state.bodyRefused !== null) return Promise.resolve();
    if (!sitesReady) {
      sitesReady = fetch(`${DTM_BASE}/sites.json`, { cache: 'no-cache' })
        .then((r) => r.json())
        .then((reg: { sites: Site[] }) => {
          // A row with no body cannot be placed, so it is dropped by name
          // rather than silently offered at whatever body is open.
          deps.siteRegistry.SITES = (reg.sites ?? []).filter((st) => {
            if (typeof st.body === 'string' && st.body !== '') return true;
            console.warn(`[atlas] site ${st.id} carries no body in sites.json; skipped`);
            return false;
          });
          // The switch test is HERE too, not only in the two helpers, so a production
          // build drops the call and the generator with it.
          if (TEST_SWITCHES) {
            const synth = syntheticSiteCount();
            const body = ladderBody();
            const real = deps.siteRegistry.SITES.filter((st) => st.body === body);
            const others = deps.siteRegistry.SITES.filter((st) => st.body !== body);
            const template = real[defaultSiteIdx(real, body)];
            if (synth > 0 && real.length > 0 && deps.syntheticSpread()) {
              deps.siteRegistry.SITES = [...others, ...real, ...deps.spreadSyntheticSites(real, synth, body)];
            } else if (synth > 0 && template) {
              const bodyRadiusM = rowById(body)?.radius_m ?? 1_737_400;
              deps.siteRegistry.SITES = [...others, ...syntheticSites(template, synth, bodyRadiusM)];
            }
          }
          deps.siteRegistry.sitesLoaded = true;
          selectSiteBody(ladderBody());
          applyUrlSite();
          renderSiteChips(true);
          syncFinder();
          if (deps.bodySwitch.state.siteRefused !== null) return;
          // The ladder is aimed from the registry, so it cannot be built until
          // this lands: no site, no open quadrangle and no hex ring.
          rebuildDescentTarget();
          if (!deps.live.disposed && deps.state.tier >= 1) deps.navigation.applyState();
        })
        .catch(() => {
          deps.siteRegistry.SITES = [];
          deps.siteRegistry.sitesLoaded = true;
          selectSiteBody(ladderBody());
          // `applyUrlSite` has no rows to resolve against and would leave a
          // named site silently unaimed.
          if (deps.bodySwitch.urlSiteId !== null && !deps.bodySwitch.state.urlSiteResolved) {
            deps.bodySwitch.state.urlSiteResolved = true;
            refuseSite(deps.bodySwitch.urlSiteId);
          }
          rebuildDescentTarget();
        });
    }
    return sitesReady;
  }
  renderSiteChips();
  sfHead.addEventListener('click', () => setFinderOpen(!finderOpen));
  pcSites.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    if (target.closest('[data-pick-clear]')) {
      pickBox = null;
      listFromTop();
      return;
    }
    if (target.closest('#pc-sites-more')) {
      foldOpen = !foldOpen;
      if (!foldOpen) globeArmed = false;
      renderSiteChips();
      return;
    }
    if (target.closest('#pc-sites-globe')) {
      globeArmed = !globeArmed;
      renderSiteChips();
      return;
    }
    const b = target.closest('button[data-site]') as HTMLElement | null;
    if (!b) return;
    const i = finderInject === 'stale' && b.dataset['pos'] !== undefined ? Number(b.dataset['pos']) : Number(b.dataset['site']);
    if (!Number.isInteger(i) || i < 0 || i >= deps.siteRegistry.bodySites.length) return;
    // Above the claim the open site is still a place to go.
    if (i === deps.siteRegistry.siteIdx && deps.state.tier >= 5) return;
    deps.bodySwitch.state.urlSiteResolved = true;
    deps.siteRegistry.siteIdx = i;
    globeArmed = false;
    // Switching site always returns to the claim: a plot id is a lattice
    // coordinate on ONE site's footprint and means nothing on another's.
    deps.state.tier = 5;
    deps.state.tileId = null;
    deps.state.quadId = null;
    deps.live.selectedSub = null;
    deps.cameraRig.stopCinema();
    // A different site is a different quadrangle and a different hex ring.
    rebuildDescentTarget();
    // Re-rendered rather than re-classed: with a filter running, the newly open
    // site may have to be pinned above the matches, and the list scrolled to it.
    renderSiteChips(true);
    deps.navigation.applyState();
  });
  pcSitesList.addEventListener('scroll', () => paintRows(), { passive: true });
  // A list built while its box was closed has no height to draw rows into; it
  // paints once the box opens, whichever panel or popup it opened in.
  const listSize = new ResizeObserver(() => {
    if (revealPending && pcSitesList.clientHeight > 0) {
      revealPending = false;
      revealSelected();
    } else paintRows();
  });
  listSize.observe(pcSitesList);
  deps.own(() => listSize.disconnect());
  function listFromTop(): void {
    revealPending = false;
    pcSitesList.scrollTop = 0;
    renderSiteChips();
    pcSitesList.scrollTop = 0;
  }
  pcSitesFilter.addEventListener('input', () => {
    siteFilter = pcSitesFilter.value.trim().toLowerCase();
    listFromTop();
  });
  for (const sel of [selNs, selEw, selChart, selSource, selKind, selSort]) {
    sel.addEventListener('change', () => {
      readFacetSelects();
      listFromTop();
    });
  }
  pcSitesFilter.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      if (pcSitesFilter.value !== '') {
        pcSitesFilter.value = '';
        siteFilter = '';
        listFromTop();
      } else if (finderOpen) {
        setFinderOpen(false);
        sfHead.focus();
      }
    } else if (ev.key === 'Enter' || ev.key === 'ArrowDown') {
      if (pcSitesList.classList.contains('rows')) {
        ev.preventDefault();
        focusRow(Math.min(shown.length - 1, Math.ceil(pcSitesList.scrollTop / rowPitchPx())));
      } else {
        const first = pcSitesList.querySelector('button[data-match="1"]') as HTMLButtonElement | null;
        if (first) {
          ev.preventDefault();
          first.focus();
        }
      }
    }
  });
  pcSitesList.addEventListener('keydown', (ev) => {
    if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
    const row = (ev.target as HTMLElement).closest('button[data-pos]') as HTMLElement | null;
    if (!row || !pcSitesList.classList.contains('rows')) return;
    ev.preventDefault();
    const pos = Number(row.dataset['pos']) + (ev.key === 'ArrowDown' ? 1 : -1);
    if (pos < 0) pcSitesFilter.focus();
    else if (pos < shown.length) focusRow(pos);
  });

  return {
    ensureSites,
    renderSiteChips,
    resetFilter(): void {
      siteFilter = '';
      pcSitesFilter.value = '';
      for (const sel of [selNs, selEw, selChart, selSource, selKind]) sel.value = '';
      readFacetSelects();
      pickBox = null;
      globeArmed = false;
    },
    syncFinder,
    closeFinder(): void { setFinderOpen(false); },
    get finderOpen(): boolean { return finderOpen; },
    get globePickArmed(): boolean { return globeArmed; },
    pickOnGlobe(lat: number, lon: number, halfKm: number): void {
      globeArmed = false;
      pickBox = boxAround(lat, lon, halfKm, bodyRadiusKm());
      const lonE = ((lon % 360) + 360) % 360;
      pickLabel = `${Math.abs(lat).toFixed(1)} ${lat >= 0 ? 'N' : 'S'} ${lonE.toFixed(1)} E, ${Math.round(2 * halfKm)} KM ACROSS`;
      revealPending = false;
      if (!finderOpen && deps.state.tier < 5) setFinderOpen(true);
      listFromTop();
    },
    probe: {
      get sites(): readonly string[] | null {
        return deps.siteRegistry.sitesLoaded ? deps.siteRegistry.bodySites.map((st) => st.id) : null;
      },
      get siteChips(): { sites: number; selected: number; filter: string; rerender: () => void } {
        return {
          sites: deps.siteRegistry.bodySites.length,
          selected: deps.siteRegistry.siteIdx,
          filter: siteFilter,
          rerender: (): void => { renderSiteChips(); },
        };
      },
      get siteFinder(): SiteFinderProbe {
        return {
          open: finderOpen,
          foldOpen,
          armed: globeArmed,
          many: deps.siteRegistry.bodySites.length > SITE_LIST_BANDS_ABOVE,
          total: deps.siteRegistry.bodySites.length,
          shown: shown.map((i) => deps.siteRegistry.bodySites[i]?.id ?? ''),
          rowsInDom: pcSitesList.querySelectorAll('button[data-site]').length,
          sort: sortBy,
          pick: pickBox,
          subShip: deps.subShipLatLon(),
          lastRenderMs,
        };
      },
    },
  };
}
